/**
 * Portaled popover for RmaListFilterControl: positioned panel and filter body content.
 * Uses Floating UI to anchor to the filter card and track resize while open.
 * Closes on outside pointerdown (no overlay), Escape, or page/sidebar scroll — scroll
 * inside the panel itself is ignored so long option lists remain usable.
 */
import React, { useEffect, useLayoutEffect } from "react";
import {
    autoUpdate,
    FloatingPortal,
    offset,
    useDismiss,
    useFloating,
    useInteractions,
} from "@floating-ui/react";
import { RmaListFilterPopoverCloseProvider } from "../rma-list-filter-popover-close-context";
import RmaListFilterControlContent, {
    RmaListFilterControlDataProps,
} from "./rma-list-filter-control-content/rma-list-filter-control-content";

interface IProps {
    isOpen: boolean;
    anchorRef: React.RefObject<HTMLElement | null>;
    filterControlDataProps: RmaListFilterControlDataProps;
    onClose: () => void;
}

const RmaListFilterControlPortal: React.FC<IProps> = ({
    isOpen,
    anchorRef,
    filterControlDataProps,
    onClose,
}) => {
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            if (!open) {
                onClose();
            }
        },
        // Open to the right of the sidebar row, aligned to its top edge.
        placement: "right-start",
        strategy: "fixed",
        middleware: [offset(8)],
        whileElementsMounted: autoUpdate,
    });

    const dismiss = useDismiss(context);
    const { getFloatingProps } = useInteractions([dismiss]);

    useLayoutEffect(() => {
        refs.setReference(anchorRef.current);
    }, [anchorRef, isOpen, refs]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onScroll = (event: Event) => {
            const target = event.target;
            if (!(target instanceof Node)) {
                onClose();
                return;
            }
            // Keep open when scrolling the panel's own overflow (e.g. long option lists).
            if (refs.floating.current?.contains(target)) {
                return;
            }
            onClose();
        };

        window.addEventListener("scroll", onScroll, true);
        return () => {
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [isOpen, onClose, refs]);

    if (!isOpen) {
        return null;
    }

    return (
        <FloatingPortal>
            <div
                ref={refs.setFloating}
                className="rma-list-filter-control__panel"
                style={floatingStyles}
                {...getFloatingProps()}
            >
                <RmaListFilterPopoverCloseProvider onRequestClose={onClose}>
                    <RmaListFilterControlContent
                        filterControlDataProps={filterControlDataProps}
                        isOpen={isOpen}
                    />
                </RmaListFilterPopoverCloseProvider>
            </div>
        </FloatingPortal>
    );
};

export default RmaListFilterControlPortal;
